import React from 'react';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import Coins from 'lucide-react/dist/esm/icons/coins.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import { charityConfig } from '../../config/charity';
import InlineTranslationEdit from '../InlineTranslationEdit';
import { useContent } from '../../contexts/ContentContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';
import { useCharityTheme } from '../../contexts/CharityThemeContext';

export default function SplitPotCharity({ jackpot = 0, charityAmount, charityName: charityNameProp, editMode = false, handleTranslationUpdate }) {
  const { c } = useContent();
  const { siteDetails } = useSiteConfig() || {};
  const { branding } = useCharityTheme();
  const brandLogo = siteDetails?.siteLogo || branding?.logoUrl || charityConfig.logoUrl;
  const brandName = charityNameProp || branding?.charityName || siteDetails?.charityName || siteDetails?.organizationName || siteDetails?.siteName || charityConfig.name || 'Our Community Partner';
  const brandWebsite = siteDetails?.charityWebsite || charityConfig.website || '#';
  const brandImpact = siteDetails?.charityDescription || charityConfig.impactStatement || `Every ticket purchased supports ${brandName} and their work in our community.`;
  const charityShare = charityAmount != null ? Math.floor(charityAmount) : Math.floor(jackpot / 2);

  return (
    <div className="relative overflow-hidden bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-200 dark:border-blue-800">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/50 to-cyan-200/50 dark:from-amber-400/20 dark:to-yellow-400/20 rounded-full blur-2xl"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-amber-200/50 to-yellow-200/50 dark:from-blue-400/20 dark:to-cyan-400/20 rounded-full blur-2xl"></div>

      <div className="relative p-6">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg shadow-md">
            <Coins className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-bold text-gray-900 dark:text-white"><InlineTranslationEdit translationKey="CharitySplitPotTitle" value={c('CharitySplitPotTitle', 'Split the Pot')} fallback="Split the Pot" onUpdate={handleTranslationUpdate} editMode={editMode} /></h3>
        </div>

        {/* 50/50 Split Display */}
        <div className="flex items-center justify-center gap-4 mb-6">
          {/* Winner's Half */}
          <div className="flex-1 text-center p-4 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 rounded-xl border-2 border-amber-400 dark:border-amber-700 shadow-sm">
            <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-wide mb-1"><InlineTranslationEdit translationKey="CharitySplitPotWinnersHalf" value={c('CharitySplitPotWinnersHalf', "Winner's Half")} fallback="Winner's Half" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
            <p className="text-2xl font-black text-amber-600 dark:text-amber-400">
              ${charityShare.toLocaleString()}
            </p>
          </div>

          {/* 50/50 Badge */}
          <div className="relative">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-sm">50/50</span>
            </div>
          </div>

          {/* Charity's Half */}
          <div className="flex-1 text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 rounded-xl border-2 border-blue-400 dark:border-blue-700 shadow-sm">
            <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wide mb-1"><InlineTranslationEdit translationKey="CharitySplitPotCharitysHalf" value={c('CharitySplitPotCharitysHalf', "Charity's Half")} fallback="Charity's Half" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
            <p className="text-2xl font-black text-blue-600 dark:text-blue-400">
              ${charityShare.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Charity Info */}
        <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700">
          <img
            src={brandLogo}
            alt={brandName}
            className="w-14 h-14 object-contain rounded-lg bg-white dark:bg-gray-700 p-1 shadow-md border border-gray-100 dark:border-gray-600"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Heart className="w-4 h-4 text-red-500" />
              <h4 className="font-bold text-gray-900 dark:text-white">{brandName}</h4>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-400 leading-relaxed">
              <InlineTranslationEdit translationKey="CharitySplitPotImpactStatement" value={c('CharitySplitPotImpactStatement', brandImpact)} fallback={brandImpact} onUpdate={handleTranslationUpdate} editMode={editMode} />
            </p>
          </div>
        </div>

        {/* Learn More Link */}
        {brandWebsite && brandWebsite !== '#' && (
        <div className="mt-4 text-center">
          <a
            href={brandWebsite}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors group"
          >
            <InlineTranslationEdit translationKey="CharitySplitPotLearnMore" value={c('CharitySplitPotLearnMore', 'Learn more about')} fallback="Learn more about" onUpdate={handleTranslationUpdate} editMode={editMode} />{' '}{brandName}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
        )}
      </div>
    </div>
  );
}
